<template>
    <div class="tw:flex tw:flex-col tw:gap-3">
        <!-- Término: Privacidad + Condiciones (obligatorio) -->
        <div class="tw:flex tw:flex-col tw:gap-1">
            <ToggleSwitch
                id="ft-privacidad"
                :value="privacidad"
                :disabled="disabled"
                :variant="mostrarError && !privacidad ? 'danger' : 'success'"
                size="sm"
                align="start"
                label-class="tw:text-slate-700 tw:normal-case tw:tracking-normal tw:leading-relaxed tw:text-[10px]"
                @input="onTogglePrivacidad"
            >
                {{ $t('formularios.he_leido') }}
                <router-link :to="{ name: 'legal' }" target="_blank" class="tw:text-slate-900 tw:font-bold tw:underline">
                    {{ $t('formularios.terminos_condiciones') }}
                </router-link>
                {{ $t('formularios.terminos_y_la') }}
                <router-link :to="{ name: 'privacidad' }" target="_blank" class="tw:text-slate-900 tw:font-bold tw:underline">
                    {{ $t('formularios.politica_privacidad') }}
                </router-link>
            </ToggleSwitch>
            <p v-if="mostrarError && !privacidad" class="tw:text-xs tw:text-red-600 tw:m-0 tw:ml-11">
                {{ $t('formularios.error_terminos') }}
            </p>
        </div>

        <!-- Término: Condiciones generales (obligatorio) -->
        <div class="tw:flex tw:flex-col tw:gap-1">
            <ToggleSwitch
                id="ft-condiciones"
                :value="condiciones"
                :variant="mostrarError && !condiciones ? 'danger' : 'success'"
                size="sm"
                align="start"
                label-class="tw:text-slate-700 tw:normal-case tw:tracking-normal tw:leading-relaxed tw:text-[10px]"
                @input="onToggleCondiciones"
            >
                {{ $t('formularios.acepto') }}
                <router-link :to="{ name: 'condiciones_generales' }" target="_blank" class="tw:text-slate-900 tw:font-bold tw:underline tw:uppercase">
                    {{ $t('formularios.condiciones') }}
                </router-link>
            </ToggleSwitch>
            <p v-if="mostrarError && !condiciones" class="tw:text-xs tw:text-red-600 tw:m-0 tw:ml-11">
                {{ $t('formularios.error_condiciones') }}
            </p>
        </div>

        <!-- Término: Comunicaciones (opcional) -->
        <div class="tw:flex tw:flex-col tw:gap-1">
            <ToggleSwitch
                id="ft-comunicaciones"
                :value="comunicaciones"
                :disabled="disabled"
                variant="success"
                size="sm"
                align="start"
                label-class="tw:text-slate-700 tw:normal-case tw:tracking-normal tw:leading-relaxed tw:text-[10px]"
                @input="onToggleComunicaciones"
            >
                {{ $t(esMalecon ? 'formularios.acepto_comunicaciones_malecon' : 'formularios.acepto_comunicaciones', { teatro: nombreTeatro }) }}
            </ToggleSwitch>
            <p v-if="comunicacionesObligatorias && mostrarError && !comunicaciones" class="tw:text-xs tw:text-red-600 tw:m-0 tw:ml-11">
                {{ $t('formularios.error_comunicaciones') }}
            </p>
        </div>

        <!-- Término: Plazos (opcional, se activa con prop pagoPlazos) -->
        <div v-if="pagoPlazos" class="tw:flex tw:flex-col tw:gap-1">
            <ToggleSwitch
                id="ft-plazos"
                :value="plazos"
                :disabled="disabled"
                :variant="mostrarError && !plazos ? 'danger' : 'success'"
                size="sm"
                align="start"
                label-class="tw:text-slate-700 tw:normal-case tw:tracking-normal tw:leading-relaxed tw:text-[10px]"
                @input="onTogglePlazos"
            >
                {{ $t('formularios.acepto_plazos') }}
                <button
                    type="button"
                    class="tw:text-slate-900 tw:font-bold tw:underline tw:uppercase tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer"
                    @click="mostrarModalPlazos = true"
                >
                    {{ $t('formularios.condiciones_plazos') }}
                </button>
                {{ $t('formularios.pago_plazos') }}
            </ToggleSwitch>
            <p v-if="mostrarError && !plazos" class="tw:text-xs tw:text-red-600 tw:m-0 tw:ml-11">
                {{ $t('formularios.error_condiciones_plazos') }}
            </p>

            <!-- Modal plazos inline (sin dependencia de vue-js-modal) -->
            <BaseModalFront
                :show="mostrarModalPlazos"
                :title="$t('formularios.condiciones_plazos')"
                :show-cancel="false"
                :confirm-text="$t('formularios.cerrar') || 'Cerrar'"
                confirm-variant="primary"
                @close="mostrarModalPlazos = false"
                @confirm="mostrarModalPlazos = false"
            >
                <div v-if="condicionesPlazos" v-html="condicionesPlazos"></div>
                <div v-else-if="textoCondiciones" v-html="textoCondiciones"></div>
            </BaseModalFront>
        </div>
    </div>
</template>

<script setup>
    /**
     * FormTerminosFront — componente reutilizable para aceptación de términos (módulo front/ui).
     *
     * Encapsula todos los textos, links y validación visual.
     * El consumidor solo pasa flags de estado y escucha el emit `update:valor`.
     *
     * Props:
     *   valor                Object   — estado { privacidad, condiciones, comunicaciones, plazos }
     *   mostrarError         Boolean  — activa feedback visual de validación
     *   disabled             Boolean  — deshabilita todos los checkboxes
     *   pagoPlazos           Boolean  — muestra el término de plazos (opcional)
     *   condicionesPlazos    String   — HTML con las condiciones de plazos (prop del evento)
     *   textoCondiciones     String   — HTML de fallback para las condiciones de plazos
     *   comunicacionesObligatorias Boolean — hace el check de comunicaciones obligatorio
     *
     * Emits:
     *   update:valor  Object  — nuevo estado completo tras cada cambio
     */
    import { ref, computed, watch, getCurrentInstance } from 'vue';
    import BaseModalFront from '@/modules/front/ui/BaseModalFront.vue';
    import ToggleSwitch from '@/modules/front/ui/ToggleSwitch.vue';

    const props = defineProps({
        valor: {
            type: Object,
            default: () => ({ privacidad: false, condiciones: false, comunicaciones: false, plazos: false }),
        },
        mostrarError: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        pagoPlazos: {
            type: Boolean,
            default: false,
        },
        condicionesPlazos: {
            type: String,
            default: '',
        },
        textoCondiciones: {
            type: String,
            default: '',
        },
        comunicacionesObligatorias: {
            type: Boolean,
            default: false,
        },
    });

    const emit = defineEmits(['update:valor']);

    // Estado local sincronizado con prop `valor`
    const privacidad = ref(props.valor?.privacidad ?? false);
    const condiciones = ref(props.valor?.condiciones ?? false);
    const comunicaciones = ref(props.valor?.comunicaciones ?? false);
    const plazos = ref(props.valor?.plazos ?? false);
    const mostrarModalPlazos = ref(false);

    // Sync si el padre resetea el valor desde fuera
    watch(() => props.valor, v => {
        privacidad.value = v?.privacidad ?? false;
        condiciones.value = v?.condiciones ?? false;
        comunicaciones.value = v?.comunicaciones ?? false;
        plazos.value = v?.plazos ?? false;
    }, { deep: true });

    function emitChange() {
        emit('update:valor', {
            privacidad: privacidad.value,
            condiciones: condiciones.value,
            comunicaciones: comunicaciones.value,
            plazos: plazos.value,
        });
    }

    // --- Explicit setter methods para evitar ambigüedad de compilador en Vue 2.7 ---

    function onTogglePrivacidad(val) {
        privacidad.value = val;
        emitChange();
    }

    function onToggleCondiciones(val) {
        condiciones.value = val;
        emitChange();
    }

    function onToggleComunicaciones(val) {
        comunicaciones.value = val;
        emitChange();
    }

    function onTogglePlazos(val) {
        plazos.value = val;
        emitChange();
    }

    // Teatro desde store (igual que legacy)
    const { proxy } = getCurrentInstance();
    const teatro = computed(() => proxy.$store.getters.getTeatro);
    const nombreTeatro = computed(() => teatro.value?.nombre || '');
    const esMalecon = computed(() => teatro.value?.hash === '7IzXuJcTytAub9imoikD');
</script>
